/**
 * Copyright 2020 (c) Dominik Basner, Sotec GmbH (for VDW e.V.)
 * Copyright 2020 (c) Fabian Wilhelm, Sotec GmbH (for VDW e.V.)
 * Copyright 2020-2024 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2022 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 * Copyright 2023 (c) Marc Fischer, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import mqtt from 'mqtt';
import MQTTSubscription from './MQTTSubscription';

interface ISubscription {
  subs: {
    [id: string]: MQTTSubscription;
  };
  last?: {
    msg: any;
    topic: string;
  };
}

export function isTopicMatch(subscribedTopic: string, topic: string): boolean {
  const topicRegexPattern = subscribedTopic
    .replaceAll('$', '\\$')
    .replaceAll('+', '(.+)')
    .replaceAll('#', '.+')
    .replaceAll('+', '[^/]+');
  const topicRegex = new RegExp(`^${topicRegexPattern}$`);
  return topic.match(topicRegex) !== null;
}

export default class MQTTWebsocket {
  mqttClient: mqtt.MqttClient;
  id: number;
  subsciptions: {
    [topic: string]: ISubscription;
  };

  constructor(brokerURL, username?: string, password?: string, id?: number) {
    this.subsciptions = {};
    const mqttConnectOptions: mqtt.IClientOptions = {
      username: username,
      password: password,
    };
    this.id = id || 0;
    console.debug(`Websocket with id ${this.id} created. Connects to ${brokerURL}`);
    this.mqttClient = mqtt.connect(brokerURL, mqttConnectOptions);
    this.bindMessageCallback();
  }

  bindMessageCallback = () => {
    this.mqttClient.on('message', (topic, payload) => this.receiveMessageCallback(topic, payload));
    this.mqttClient.on('error', (error) => {
      console.log(error);
    });
  };

  receiveMessageCallback = (topic, payload) => {
    let called = false;
    for (const subscribedTopic of Object.keys(this.subsciptions)) {
      if (isTopicMatch(subscribedTopic, topic)) {
        this.callSubscriptionComponentsCallback(payload, subscribedTopic, topic);
        called = true;
      }
    }
    if (!called) {
      console.log(`No subscriber found: ${topic}`);
    }
  };

  callSubscriptionComponentsCallback = (payload, topicKey: string, topic: string) => {
    const msg = this.decodeUtf8(payload);
    if (!this.subsciptions[topicKey]) {
      return;
    }
    for (const id in this.subsciptions[topicKey].subs) {
      this.subsciptions[topicKey].subs[id].subscriptionReceivesMessageCallback(msg, topic);
    }
    this.subsciptions[topicKey].last = {
      msg,
      topic: topicKey,
    };
  };

  subscribe(topic, callback) {
    if (typeof topic !== 'string') {
      throw new Error('Expect topic to be a string!');
    }

    const subscription = this.initSubscriptionArray(topic, callback);
    const last = this.subsciptions[topic]?.last;
    if (subscription.needSubscription === true) {
      console.debug(`MQTT: Subscribed ${topic}`);
      this.mqttClient.subscribe(topic, { qos: 0 }, function (err /*, granted */) {
        if (err) {
          console.log(`Mqtt Subscribe Error: ${err}`);
        }
      });
    } else if (last !== undefined) {
      subscription.subscription.subscriptionReceivesMessageCallback(last.msg, last.topic);
    }

    return subscription.subscription;
  }

  initSubscriptionArray(topic, callback) {
    let needSubscription = false;
    if (this.subsciptions[topic] === undefined) {
      this.subsciptions[topic] = { subs: {} };
      this.subsciptions[topic].subs = {};
      needSubscription = true;
    }

    const subscription = new MQTTSubscription(topic, callback, this);
    this.subsciptions[topic].subs[subscription.id] = subscription;

    return {
      needSubscription,
      subscription,
    };
  }

  unsubscribe(topic: string, id: string) {
    if (
      this.subsciptions[topic] === undefined ||
      this.subsciptions[topic].subs === undefined ||
      this.subsciptions[topic].subs[id] === undefined
    ) {
      return;
    }
    delete this.subsciptions[topic].subs[id];
    if (Object.keys(this.subsciptions[topic].subs).length === 0) {
      this.mqttClient.unsubscribe(topic);
      console.debug(`MQTT: Unsubscribed ${topic}`);
      delete this.subsciptions[topic];
    }
  }

  decodeUtf8(payload) {
    return new TextDecoder('utf-8').decode(payload);
  }
}
